import {
	Card,
	Table,
	Button,
	Tag,
	Space,
	Select,
	Row,
	Col,
	Typography,
	notification,
	Tooltip,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	PiPlusDuotone,
	PiPencilDuotone,
	PiEyeDuotone,
	PiShoppingCartDuotone,
	PiCalendarDuotone,
	PiHouseDuotone,
	PiPackageDuotone,
} from "react-icons/pi";
import AppContext from "../../store/app.context";
import { requestGetOrderList } from "../../services/admin.service";
import SearchInput from "../../components/SearchInput";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Formatter";

const { Title, Text } = Typography;
const { Option } = Select;

function OrdersListPage() {
	const context = useContext(AppContext);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [orders, setOrders] = useState([]);

	// Initialize state from URL parameters
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});
	const [filters, setFilters] = useState({
		keyword: searchParams.get("keyword") || "",
		status: searchParams.get("status") || "",
		currency: searchParams.get("currency") || "",
	});

	useEffect(() => {
		document.title = "Cloudiva Console | Orders";
		fetchOrders();
	}, []);

	useEffect(() => {
		fetchOrders();
	}, [pagination.current, pagination.pageSize, filters]);

	// Update URL parameters when state changes
	useEffect(() => {
		const params = new URLSearchParams();

		// Add pagination parameters
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());

		// Add filter parameters
		if (filters.keyword) params.set("keyword", filters.keyword);
		if (filters.status) params.set("status", filters.status);
		if (filters.currency) params.set("currency", filters.currency);

		// Update URL without triggering navigation
		setSearchParams(params, { replace: true });
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const fetchOrders = () => {
		setLoading(true);

		// Build filter string from filter object
		const filterParts = [];
		if (filters.status) {
			filterParts.push(`status:${filters.status}`);
		}
		// Add currency filter if needed
		// if (filters.currency) filterParts.push(`currency:${filters.currency}`);

		const filterString = filterParts.join(",");

		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.keyword,
			filter: filterString,
			order: "issuedAt:desc",
		};

		requestGetOrderList(params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const ordersData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setOrders(ordersData);
				setPagination((prev) => ({
					...prev,
					total: serverPagination.total || 0,
				}));
			})
			.catch((error) => {
				console.error("Error fetching orders:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch orders",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleTableChange = (newPagination) => {
		setPagination(newPagination);
	};

	const handleSearch = (keyword) => {
		setFilters((prev) => ({ ...prev, keyword }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleStatusFilter = (status) => {
		setFilters((prev) => ({ ...prev, status }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleCurrencyFilter = (currency) => {
		setFilters((prev) => ({ ...prev, currency }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "PENDING":
				return "orange";
			case "CONFIRMED":
				return "blue";
			case "COMPLETED":
				return "green";
			case "CANCELLED":
				return "red";
			case "FAILED":
				return "red";
			default:
				return "default";
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case "PENDING":
				return "Pending";
			case "CONFIRMED":
				return "Confirmed";
			case "COMPLETED":
				return "Completed";
			case "CANCELLED":
				return "Cancelled";
			case "FAILED":
				return "Failed";
			default:
				return status;
		}
	};

	const columns = [
		{
			title: "Order Number",
			dataIndex: "number",
			key: "number",
			render: (text, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiShoppingCartDuotone size={20} style={{ marginRight: 8, color: "#1890ff" }} />
					<div>
						<div style={{ fontWeight: 500 }}>{text}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>
							{record.items?.length || 0} item{record.items?.length !== 1 ? "s" : ""}
						</div>
					</div>
				</div>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status, record) => (
				<Space size={2} direction="vertical">
					<Tag color={getStatusColor(status)} style={{ borderRadius: "12px" }}>
						{getStatusText(status)}
					</Tag>
					{status === "PENDING" && dayjs(record.expiredAt).isBefore(dayjs().add(60, "day")) && (
						<Tag color="#ff4d4f" style={{ borderRadius: "12px" }}>
							Expiring in {dayjs(record.expiredAt).diff(dayjs(), "day")} days
						</Tag>
					)}
				</Space>
			),
		},
		{
			title: "Total",
			dataIndex: "total",
			key: "total",
			render: (amount, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<div>
						<div style={{ fontWeight: 500 }}>{formatCurrency(amount, record.currency)}</div>
						{record.discountTotal > 0 && (
							<div style={{ fontSize: "12px", color: "#666" }}>
								Discount: {formatCurrency(record.discountTotal, record.currency)}
							</div>
						)}
					</div>
				</div>
			),
		},
		{
			title: "Currency",
			dataIndex: "currency",
			key: "currency",
			render: (currency) => <Tag color="blue">{currency}</Tag>,
		},
		{
			title: "Billing Contact",
			dataIndex: "billingAddress",
			key: "billingAddress",
			render: (billingAddress) => (
				<div>
					<div style={{ fontWeight: 500 }}>{billingAddress?.name}</div>
					<div style={{ fontSize: "12px", color: "#666" }}>{billingAddress?.email}</div>
				</div>
			),
		},
		{
			title: "Dates",
			key: "dates",
			render: (_, record) => (
				<div>
					<div style={{ fontSize: "12px" }}>
						<PiCalendarDuotone color="#52c41a" />{" "}
						{dayjs(record.issuedAt).format("DD MMMM YYYY HH:mm")}
					</div>
					<div style={{ fontSize: "12px", color: "#666" }}>
						<PiCalendarDuotone color="#ff4d4f" />{" "}
						{dayjs(record.expiredAt).format("DD MMMM YYYY HH:mm")}
					</div>
				</div>
			),
		},
		{
			title: "Action",
			key: "action",
			align: "right",
			render: (_, record) => (
				<Space size="middle">
					<Tooltip title="View Order">
						<Button
							type="text"
							size="small"
							icon={<PiEyeDuotone />}
							onClick={() => navigate(`/orders/${record._id}`)}
						/>
					</Tooltip>
					<Tooltip title="Edit Order">
						<Button
							type="text"
							size="small"
							icon={<PiPencilDuotone />}
							onClick={() => navigate(`/orders/${record._id}/edit`)}
						/>
					</Tooltip>
				</Space>
			),
		},
	];

	// Expandable row render for order items
	const expandedRowRender = (record) => {
		const itemColumns = [
			{
				title: "Item Details",
				key: "itemDetails",
				render: (_, item) => {
					if (item.type === "DOMAIN") {
						// Domain item
						return (
							<div style={{ display: "flex", alignItems: "center" }}>
								<PiPackageDuotone size={16} style={{ marginRight: 8, color: "#52c41a" }} />
								<div>
									<div style={{ fontWeight: 500 }}>Domain {item.domain?.name || "N/A"}</div>
									<div style={{ fontSize: "12px", color: "#666" }}>
										{item.domain?.extension?.name} ({item.domain?.extension?.code})
										{item.domain?._id && ` • ID: ${item.domain._id}`}
									</div>
								</div>
							</div>
						);
					}
					// Instance item
					return (
						<div style={{ display: "flex", alignItems: "center" }}>
							<PiPackageDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
							<div>
								<div style={{ fontWeight: 500 }}>{item.instance?.service?.name || "N/A"}</div>
								<div style={{ fontSize: "12px", color: "#666" }}>
									{item.instance?.plan?.name} ({item.instance?.plan?.code})
									{item.instance?.region?.name && ` • ${item.instance.region.name}`}
									{item.instance?._id && ` • Instance: ${item.instance._id}`}
								</div>
							</div>
						</div>
					);
				},
			},
			{
				title: "Quantity",
				dataIndex: "quantity",
				key: "quantity",
				render: (quantity, item) => (
					<div>
						<div>{quantity}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>{item.plan?.price?.interval}</div>
					</div>
				),
			},
			{
				title: "Line Total",
				dataIndex: "lineTotal",
				key: "lineTotal",
				render: (lineTotal) => formatCurrency(lineTotal, record.currency),
			},
		];

		return (
			<Table
				columns={itemColumns}
				dataSource={record.items}
				pagination={false}
				size="small"
				rowKey={(item, index) => `${record._id}-${index}`}
			/>
		);
	};

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiHouseDuotone />} onClick={() => navigate("/")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Order Management
					</Title>
				</Space>
			</div>
			<div className="page-content-wrapper">
				<Card
					title="Orders"
					extra={
						<Button type="primary" icon={<PiPlusDuotone />} onClick={() => navigate("/orders/add")}>
							Add Order
						</Button>
					}>
					<Row gutter={16} style={{ marginBottom: 16 }}>
						<Col span={6}>
							<SearchInput
								placeholder="Search orders..."
								onSearch={handleSearch}
								style={{ width: "100%" }}
							/>
						</Col>
						<Col span={4}>
							<Select
								placeholder="Status"
								onChange={handleStatusFilter}
								allowClear
								style={{ width: "100%" }}>
								<Option value="PENDING">Pending</Option>
								<Option value="CONFIRMED">Confirmed</Option>
								<Option value="COMPLETED">Completed</Option>
								<Option value="CANCELLED">Cancelled</Option>
								<Option value="FAILED">Failed</Option>
							</Select>
						</Col>
						<Col span={4}>
							<Select
								placeholder="Currency"
								onChange={handleCurrencyFilter}
								allowClear
								style={{ width: "100%" }}>
								<Option value="IDR">IDR</Option>
								<Option value="SGD">SGD</Option>
								<Option value="AUD">AUD</Option>
								<Option value="USD">USD</Option>
								<Option value="EUR">EUR</Option>
								<Option value="GBP">GBP</Option>
							</Select>
						</Col>
					</Row>
					<Table
						columns={columns}
						dataSource={orders}
						loading={loading}
						pagination={{
							...pagination,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
						}}
						onChange={handleTableChange}
						expandable={{
							expandedRowRender,
							rowExpandable: (record) => record.items && record.items.length > 0,
						}}
						rowKey="_id"
					/>
				</Card>
			</div>
		</div>
	);
}

export default OrdersListPage;
